import React, { useEffect, useRef } from "react";
import { HeroSectionStyled } from "./HeroSectionStyled";
import HPToner from "../../../../assets/images/HPToner.png";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const shapesRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const section = sectionRef.current;
    const tagline = taglineRef.current;
    const title = titleRef.current;
    const paragraph = paragraphRef.current;
    const cta = ctaRef.current;
    const image = imageRef.current;

    if (!section || !title || !paragraph) return;

    const ctx = gsap.context(() => {
      // Timeline for sequenced animations
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      // Tagline animation
      if (tagline) {
        tl.fromTo(tagline, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 });
      }

      // Title animation
      tl.fromTo(
        title,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8 },
        "-=0.3"
      );

      // Paragraph animation
      tl.fromTo(
        paragraph,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6 },
        "-=0.4"
      );

      // CTA buttons animation
      if (cta) {
        tl.fromTo(
          cta.children,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.15 },
          "-=0.3"
        );
      }

      // Image animation
      if (image) {
        tl.fromTo(
          image,
          { opacity: 0, x: 100, scale: 0.9 },
          { opacity: 1, x: 0, scale: 1, duration: 1 },
          "-=0.8"
        );

        // Parallax on scroll
        gsap.to(image, {
          y: 80,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        });
      }

      // Animate decorative shapes
      shapesRef.current.forEach((shape, index) => {
        if (shape) {
          gsap.fromTo(
            shape,
            { opacity: 0, scale: 0 },
            {
              opacity: shape.classList.contains("shape-3") ? 0.5 : 0.7,
              scale: 1,
              duration: 0.8,
              delay: 0.5 + index * 0.2,
              ease: "back.out(1.7)",
            }
          );
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  const addShapeRef = (el: HTMLDivElement | null, index: number) => {
    if (el) shapesRef.current[index] = el;
  };

  return (
    <HeroSectionStyled ref={sectionRef}>
      {/* Decorative shapes */}
      <div className="shape-1" ref={(el) => addShapeRef(el, 0)} />
      <div className="shape-2" ref={(el) => addShapeRef(el, 1)} />
      <div className="shape-3" ref={(el) => addShapeRef(el, 2)} />
      <div className="shape-4" ref={(el) => addShapeRef(el, 3)} />

      <div className="heroSectionContainer">
        <div className="heroContent">
          <div className="tagline" ref={taglineRef}>
            <div className="badgeWrapper">
              <span className="hpBadge">
                <span className="badgeIcon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="checkIcon">
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                <span className="badgeText">HP</span>
              </span>
              <div className="shimmer" />
            </div>
            <div className="taglineDivider" />
            <span className="taglineText">
              <span className="word">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Authorized
              </span>
              <span className="word">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
                Service
              </span>
              <span className="word">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Center
              </span>
            </span>
            <span className="verifiedIcon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </span>
            <div className="badgeContact">
              <a href="tel:+96522645937" className="badgeContactIcon" title="Call us">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                </svg>
              </a>
              <a href="mailto:sales@saharacomputers.com" className="badgeContactIcon" title="Email us">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
            </div>
          </div>

          <h1 className="heroTitle" ref={titleRef}>
            Your <span className="highlight">satisfaction</span> is our inspiration.
          </h1>

          <p className="heroParagraph" ref={paragraphRef}>
            Welcome to our one-stop technology hub, where expertise meets
            excellence. Elevate your tech experience with expert support and
            quality products from leading brands.
          </p>

          <div className="heroCTA" ref={ctaRef}>
            <a href="#contactUs" className="primaryBtn">
              Get Started
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a href="#services" className="secondaryBtn">
              Our Services
            </a>
          </div>

          <div className="heroContact">
            <a href="tel:+96522645937" className="contactLink">
              <span className="contactIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                </svg>
              </span>
              <span className="contactText">+965 2264 5937</span>
            </a>
            <span className="contactDivider">|</span>
            <a href="mailto:sales@saharacomputers.com" className="contactLink">
              <span className="contactIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <span className="contactText">sales@saharacomputers.com</span>
            </a>
          </div>
        </div>

        <div className="imageContainer" ref={imageRef}>
          <div className="imageBg" />
          <div className="imageWrapper">
            {/* Modern Image Grid Layout */}
            <div className="heroImageGrid">
              {/* Main Large Image - HP Printer */}
              <div className="mainImage">
                <img
                  src="https://images.pexels.com/photos/4792733/pexels-photo-4792733.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="HP Printer Services"
                />
                <div className="imageOverlay">
                  <span className="overlayBadge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    HP Authorized
                  </span>
                </div>
              </div>

              {/* Secondary Images */}
              <div className="secondaryImages">
                <div className="imageCard card1">
                  <img
                    src="https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=400"
                    alt="Laptop"
                  />
                  <div className="cardLabel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="2" y1="20" x2="22" y2="20" />
                    </svg>
                    Laptops
                  </div>
                </div>

                <div className="imageCard card2">
                  <img
                    src="https://images.pexels.com/photos/1029757/pexels-photo-1029757.jpeg?auto=compress&cs=tinysrgb&w=400"
                    alt="Desktop Computer"
                  />
                  <div className="cardLabel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    Desktops
                  </div>
                </div>

                <div className="imageCard card3">
                  <img
                    src={HPToner}
                    alt="Printer Consumables"
                  />
                  <div className="cardLabel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 6 2 18 2 18 9" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Consumables
                  </div>
                </div>
              </div>

              {/* Floating Stats Badge */}
              <div className="floatingBadge">
                <div className="badgeNumber">25+</div>
                <div className="badgeLabel">Years of Excellence</div>
              </div>

              {/* Decorative Elements */}
              <div className="decorDot dot1" />
              <div className="decorDot dot2" />
              <div className="decorCircle" />
            </div>
          </div>
        </div>
      </div>

      <div className="scrollIndicator">
        <div className="mouse" />
        <span>Scroll</span>
      </div>
    </HeroSectionStyled>
  );
};

export default HeroSection;
